'use client';

/* ──────────────────────────────────────────────
 *  ImageGallery — vignette cliquable + lightbox (vue
 *  en grand) avec navigation quand il y a plusieurs
 *  photos. Autonome et thémé `bt-*`. Partagé par le
 *  tunnel de réservation (prestations) et le tunnel
 *  carte cadeau (forfaits). Accepte aussi bien la forme
 *  `{ preview_url, original_url }` (CroppableImageResponse)
 *  que `{ small_url, medium_url, large_url }` (Thumbor).
 *
 *  La lightbox est rendue via portal HORS de `.bt-root` :
 *  la racine déclare `container-type: inline-size`
 *  (layout containment) qui ferait d'elle le containing
 *  block du backdrop `position: fixed` — voile partiel et
 *  centrage cassé sinon (même contrainte que la modale
 *  vidéo de ServiceCard). La cible exacte est résolue par
 *  `useOverlayPortalTarget` (<body>, ou la racine du shadow
 *  root dans le widget embarqué — sinon la lightbox sortirait
 *  de la feuille de style et tomberait en vrac sur la page
 *  hôte). Rendue uniquement après interaction.
 * ────────────────────────────────────────────── */

import {
  useEffect,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useOverlayPortalTarget } from '../../utils/overlay-portal';

export interface GalleryImage {
  preview_url?: string;
  original_url?: string;
  small_url?: string;
  medium_url?: string;
  large_url?: string;
}

export interface ImageGalleryLabels {
  close: string;
  previous: string;
  next: string;
}

const pickThumb = (img: GalleryImage): string | undefined =>
  img.preview_url ?? img.medium_url ?? img.small_url ?? img.large_url ?? img.original_url;

const pickLarge = (img: GalleryImage): string | undefined =>
  img.original_url ?? img.large_url ?? img.preview_url ?? img.medium_url ?? img.small_url;

export function ImageGallery({
  pictures,
  alt,
  labels,
  className,
}: {
  pictures: GalleryImage[];
  alt: string;
  labels: ImageGalleryLabels;
  /** Classe(s) supplémentaire(s) fusionnée(s) sur la vignette déclencheuse (styling par le consommateur). */
  className?: string;
}): JSX.Element | null {
  const images = pictures.filter((p): boolean => Boolean(pickThumb(p)));
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const isOpen = openIndex !== null;
  const portalTarget = useOverlayPortalTarget(triggerRef, isOpen);

  const step = (delta: number): void =>
    setOpenIndex((i): number | null => (i === null ? null : (i + delta + images.length) % images.length));
  const close = (): void => setOpenIndex(null);

  // Déplace le focus dans la dialog à l'ouverture, le restaure sur le déclencheur à la fermeture.
  useEffect((): (() => void) | undefined => {
    if (!isOpen) return undefined;
    const trigger = triggerRef.current;
    dialogRef.current?.focus();
    return (): void => trigger?.focus();
  }, [isOpen]);

  // Navigation clavier (Échap / flèches) quand la lightbox est ouverte.
  useEffect((): (() => void) | undefined => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return (): void => window.removeEventListener('keydown', onKey);
  }, [isOpen, images.length]);

  if (images.length === 0) return null;

  const thumbSrc = pickThumb(images[0]);
  // openIndex n'est jamais réconcilié avec `images` : si `pictures` rétrécit pendant que la
  // lightbox est ouverte, l'index peut sortir des bornes → on garde sur `current` (jamais undefined).
  const current = openIndex !== null ? images[openIndex] : undefined;
  const largeSrc = current ? pickLarge(current) : undefined;
  const hasMany = images.length > 1;
  const largeAlt = hasMany && openIndex !== null ? `${alt} - ${openIndex + 1}/${images.length}` : alt;

  const stop = (e: MouseEvent): void => e.stopPropagation();

  // Piège à focus minimal : Tab/Shift+Tab boucle sur les boutons de la dialog.
  const onDialogKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'Tab') return;
    const buttons = dialogRef.current?.querySelectorAll<HTMLElement>('button');
    if (!buttons || buttons.length === 0) return;
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    // Dans un shadow root, `document.activeElement` renvoie l'hôte, pas le bouton focalisé :
    // on interroge la racine de la dialog pour que le piège à focus tienne aussi dans le widget.
    const root = dialogRef.current?.getRootNode();
    const active = (root as Document | ShadowRoot | undefined)?.activeElement ?? document.activeElement;
    if (e.shiftKey && (active === first || active === dialogRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`bt-gallery-thumb${className ? ` ${className}` : ''}`}
        onClick={(e): void => {
          e.stopPropagation();
          setOpenIndex(0);
        }}
        aria-label={alt}
      >
        <img className="bt-gallery-thumb__img" src={thumbSrc} alt={alt} loading="lazy" />
        {hasMany && (
          <span className="bt-gallery-thumb__badge">
            <ImagesIcon />
            {images.length}
          </span>
        )}
      </button>

      {current &&
        largeSrc &&
        portalTarget &&
        createPortal(
          <div
            ref={dialogRef}
            className="bt-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={alt}
            tabIndex={-1}
            onClick={close}
            onKeyDown={onDialogKeyDown}
          >
            <button type="button" className="bt-lightbox__close" onClick={close} aria-label={labels.close}>
              ✕
            </button>

            {hasMany && (
              <button
                type="button"
                className="bt-lightbox__nav bt-lightbox__nav--prev"
                aria-label={labels.previous}
                onClick={(e): void => {
                  stop(e);
                  step(-1);
                }}
              >
                ‹
              </button>
            )}

            <img className="bt-lightbox__img" src={largeSrc} alt={largeAlt} onClick={stop} />

            {hasMany && (
              <>
                <button
                  type="button"
                  className="bt-lightbox__nav bt-lightbox__nav--next"
                  aria-label={labels.next}
                  onClick={(e): void => {
                    stop(e);
                    step(1);
                  }}
                >
                  ›
                </button>
                <span className="bt-lightbox__counter" aria-live="polite">
                  {openIndex + 1} / {images.length}
                </span>
              </>
            )}
          </div>,
          portalTarget,
        )}
    </>
  );
}

function ImagesIcon(): JSX.Element {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.6-3.6a2 2 0 0 0-2.8 0L6 21" />
    </svg>
  );
}
